import { ContextMenu, Menu, MenuItem, MenuPopover } from '@vizit/atoms';
import { Edit03Icon, Trash01Icon } from '@vizit/icons';
import { ContextMenuMode, contextMenuTriggerClasses } from '@vizit/molecules';
import { colors, cx } from '@vizit/theme';
import * as React from 'react';
import { CollectionEditDialog } from '../collection-edit-dialog';
import { DeleteCollectionDialog } from '../delete-collection-dialog';

interface CollectionMenuDropdownProps {
  /**
   * The ID of the collection
   */
  collectionId: string;
  /**
   * The current name of the collection
   */
  collectionName: string;
  /**
   * The ID of the portfolio
   */
  portfolioId?: string;
  /**
   * The ID of the organization
   */
  organizationId?: string;
  /**
   * Visual mode for the trigger button
   * - light: white background (for use near breadcrumbs)
   * - dark: dark background (for use on CollectionCard hover)
   */
  mode?: ContextMenuMode;
  /**
   * Whether to show the trigger button
   */
  isVisible?: boolean;
  /**
   * Custom className for the trigger button
   */
  triggerClassName?: string;
  /**
   * Callback when the collection is successfully renamed
   * Can be async to support waiting for mutations to complete
   */
  onSuccessfulRename: (newName: string) => void | Promise<void>;
  /**
   * Callback when the collection is successfully deleted
   * Can be async to support waiting for mutations to complete
   */
  onSuccessfulDelete: (collectionId: string) => void | Promise<void>;
}

function CollectionMenuDropdown(props: CollectionMenuDropdownProps) {
  const {
    collectionId,
    collectionName,
    portfolioId,
    organizationId,
    mode = ContextMenuMode.Dark,
    isVisible = true,
    triggerClassName,
    onSuccessfulRename,
    onSuccessfulDelete,
  } = props;

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const handleMenuAction = React.useCallback((key: React.Key) => {
    if (key === 'rename') {
      setIsRenameDialogOpen(true);
    } else if (key === 'delete') {
      setIsDeleteDialogOpen(true);
    }
  }, []);

  const handleDelete = React.useCallback(
    async (id: string) => {
      // Wait for mutation to complete before closing dialog
      await onSuccessfulDelete(id);
      setIsDeleteDialogOpen(false);
    },
    [onSuccessfulDelete],
  );

  const handleRename = React.useCallback(
    async (newName: string) => {
      // Wait for mutation to complete before closing dialog
      await onSuccessfulRename(newName);
      setIsRenameDialogOpen(false);
    },
    [onSuccessfulRename],
  );

  return (
    <>
      <ContextMenu
        onOpenChange={setIsMenuOpen}
        triggerProps={{
          isHovered: isVisible,
          isMenuOpen,
          className: cx(contextMenuTriggerClasses({ mode }), triggerClassName),
        }}
      >
        <MenuPopover>
          <Menu onAction={handleMenuAction}>
            <MenuItem id="rename" className="font-medium" textValue="Rename collection">
              <Edit03Icon size={16} color={colors['gray-utility'][400]} />
              Rename collection
            </MenuItem>
            <MenuItem id="delete" className="font-medium" textValue="Delete collection">
              <Trash01Icon size={16} color={colors['gray-utility'][400]} />
              Delete collection
            </MenuItem>
          </Menu>
        </MenuPopover>
      </ContextMenu>

      <CollectionEditDialog
        collectionId={collectionId}
        currentName={collectionName}
        portfolioId={portfolioId}
        organizationId={organizationId}
        isOpen={isRenameDialogOpen}
        onOpenChange={setIsRenameDialogOpen}
        onSuccessfulRename={handleRename}
        hideTrigger
      />

      <DeleteCollectionDialog
        collectionId={collectionId}
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
      />
    </>
  );
}

export { CollectionMenuDropdown };
export type { CollectionMenuDropdownProps };
